import { DynamicComponentExitReason } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import type { DialogOpenedChangedEvent } from '@vaadin/dialog';
import { dialogFooterRenderer, dialogHeaderRenderer, dialogRenderer } from '@vaadin/dialog/lit.js';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { closeIcon } from '../../assets/icons';
import { TmBaseComponent } from '../../tm-base.component';
import {
    dispatchDialogCloseEvent,
    listenForDialogResult,
    TmDialogButton,
    TmDialogButtonPosture,
    type TmDialogOrientation,
} from './tm-dialog.service.types';

import '@vaadin/dialog/src/vaadin-dialog.js';
import '@vaadin/horizontal-layout';
import '@vaadin/vertical-layout';
import '../../components/tm-button';

export const tagName = 'tm-dialog';

/**
 * Dialog component to be used with `TmDialogService`.
 *
 * @todo Rename when the existing `TmDialog` has been replaced with this one.
 */
@customElement(tagName)
export class TmDialog extends TmBaseComponent {
    @property({
        reflect: false,
        attribute: false,
    })
    public data?: unknown;

    @property({
        reflect: false,
        attribute: false,
    })
    public content?: string | TemplateResult;

    @property({
        type: String,
    })
    public header = '';

    @property({
        type: Boolean,
    })
    public preventClose = true;

    @property({
        type: Boolean,
    })
    public showDivider = true;

    @property({
        type: Boolean,
    })
    public showButtons = true;

    @property({
        reflect: false,
    })
    public buttons: TmDialogButton[] = [];

    @property({
        type: String,
        reflect: true,
    })
    public orientation: TmDialogOrientation = 'vertical';

    @property({
        type: Boolean,
    })
    public showCloseButton = false;

    @property()
    public classes: string[] = [];

    @property({ attribute: false })
    public headerIcon?: TemplateResult<2>;

    @state()
    private dialogOpen = true;

    private readonly buttonContainerRef = createRef<HTMLDivElement>();

    public render() {
        return html`<vaadin-dialog
            .opened=${this.dialogOpen}
            ?no-close-on-outside-click=${this.preventClose}
            ?no-close-on-esc=${this.preventClose}
            @opened-changed="${(e: DialogOpenedChangedEvent) => {
                const { detail } = e;
                e.preventDefault();

                if (!detail.value) {
                    this.close(DynamicComponentExitReason.ForceClose);
                }
            }}"
            ${this.renderHeader()}
            ${this.renderBody()}
            ${this.renderFooter()}
        >
        </vaadin-dialog>`;
    }

    /**
     * Convenience method for retrieving button elements.
     * Mostly used for tests.
     */
    public getButtons() {
        const { value: buttonContainer } = this.buttonContainerRef;
        if (!buttonContainer) {
            return [];
        }

        const buttonList = buttonContainer.querySelectorAll('tm-button');
        return buttonList.length > 1 ? Array.from(buttonList) : [];
    }

    private renderButtons<T>(buttons: TmDialogButton[]) {
        if (buttons.length < 1) {
            return nothing;
        }

        const isVertical = this.orientation === 'vertical';
        let buttonTemplates: TemplateResult[] = [];

        buttonTemplates = buttons.map(button => {
            const { reason, onClick, label, checkDisabled, destructive } = button;
            const posture = button.posture || TmDialogButtonPosture.Primary;
            const isDisabled = checkDisabled ? checkDisabled(this.data as T) : false;
            const clickHandler = onClick || (() => this.close(reason));
            const classes = {
                'p-1': true,
                'w-full': isVertical,
            };
            const styles = {
                // tailwind order utility doesn't work in this case, so use inline styles
                order: button.order ?? postureToOrder(posture, this.orientation),
            };

            return html`<tm-button
                class=${classMap(classes)}
                style=${styleMap(styles)}
                ?disabled=${isDisabled}
                .importance=${posture}
                .error=${ifDefined(destructive)}
                @click=${() => {
                    clickHandler(data => this.close(reason, data));
                }}
                >${label}</tm-button
            >`;
        });

        const classes = {
            'w-full': isVertical,
            'flex-wrap': true,
            'justify-end': true,
            'gap-1': true,
        };

        return isVertical
            ? html`<vaadin-vertical-layout
                  ${ref(this.buttonContainerRef)}
                  class=${classMap(classes)}
              >
                  ${buttonTemplates}
              </vaadin-vertical-layout> `
            : html`<vaadin-horizontal-layout
                  ${ref(this.buttonContainerRef)}
                  class=${classMap(classes)}
              >
                  ${buttonTemplates}
              </vaadin-horizontal-layout> `;
    }

    private renderHeader() {
        const classes = {
            flex: true,
            'flex-grow': true,
            'gap-2': true,
            'pb-4': true,
            'font-medium': true,
            'border-b': this.showDivider,
            'justify-center': this.orientation === 'vertical',
            'justify-start': this.orientation === 'horizontal',
            relative: true,
        };
        const icon = this.headerIcon ? html`<tm-icon .icon=${this.headerIcon}></tm-icon>` : nothing;
        const closeButton = this.showCloseButton
            ? html`<tm-icon
                  .icon=${closeIcon}
                  color="var(--jh-color-content-primary-enabled)"
                  class="mr-2 absolute top-0 right-0 cursor-pointer"
                  aria-label="Close dialog"
                  @click=${() => this.close(DynamicComponentExitReason.Cancel)}
              >
              </tm-icon>`
            : nothing;
        const renderer = () => {
            return html`<div class="${classMap(classes)}">
                ${icon} ${this.header} ${closeButton}
            </div>`;
        };

        return dialogHeaderRenderer(renderer);
    }

    private renderBody() {
        const content = this.content ? this.content : nothing;
        let classes: Record<string, boolean> = {};
        if (this.classes.length > 0) {
            classes = this.classes.reduce(
                (map, className) => {
                    map[className] = true;
                    return map;
                },
                {} as Record<string, boolean>
            );
        }

        return dialogRenderer(
            () =>
                html`<div
                    ${ref(element => this.onContentReady(element))}
                    class=${classMap(classes)}
                >
                    ${content}
                </div>`
        );
    }

    private renderFooter() {
        if (!this.showButtons) {
            return nothing;
        }

        return dialogFooterRenderer(() => this.renderButtons(this.buttons), this.data);
    }

    private close<T>(reason: DynamicComponentExitReason, data?: T) {
        this.dialogOpen = false;

        data = data || (this.data as T);

        const result = {
            data,
            reason,
        };

        dispatchDialogCloseEvent(this, result);
    }

    private async onContentReady(element?: Element) {
        if (!element) {
            return;
        }

        const { unsubscribe } = listenForDialogResult(element, result => {
            const { data, reason } = result;
            this.close(reason, data);
        });

        this.addCleanupTasks(unsubscribe);
    }
}

function postureToOrder(posture: TmDialogButtonPosture, orientation: TmDialogOrientation): number {
    const isVertical = orientation === 'vertical';
    const postureOrderMap: Record<TmDialogButtonPosture, number> = {
        [TmDialogButtonPosture.Primary]: isVertical ? 1 : 3,
        [TmDialogButtonPosture.Secondary]: isVertical ? 2 : 2,
        [TmDialogButtonPosture.Tertiary]: isVertical ? 3 : 1,
    };
    return postureOrderMap[posture] || 99;
}
